import {WizErrorManager} from '../errors/wiz-error.manager';
import {IWIZFocusable} from '../../../interfaces/iwiz-focusable';
import {ElementRef} from '@angular/core';
import { FOCUS_MODE, VALUE_TAB } from '@wizeoo/akm-core-lib';

/**
 * Global focus manager. Able to lock focus on invalid component if desired.
 */
export class WizFocusManager {

  constructor() {
  }

  // singleton
  private static instance: WizFocusManager = null;
  public static getInstance(): WizFocusManager {
    if (WizFocusManager.instance === null) {
      WizFocusManager.instance = new WizFocusManager();
    }
    return WizFocusManager.instance;
  }


  /** get the current focused component */
  private _currentFocused: IWIZFocusable;
  public get currentFocused(): IWIZFocusable {
    return this._currentFocused;
  }

  /** appel externe pour déterminer si le compo a le focus (numericInput pour rendu) */
  public iHaveFocus(compo: IWIZFocusable):boolean {
    return this._currentFocused === compo;
  }

  /** neutralisation du clic s'il faut neutraliser le mouseDown (mode = STOP) */
  private clickOutsidePrevented: boolean;

  /** référence à la racine */
  private root: ElementRef;

  /**
   * map d'enfants susceptible de prendre MON focus...
   * Clean sur enregistrement d'un composant (pour éviter de remplacer currentFocused)
   * la clé : ElementRef.nativeElement (le compo physique)
   * la valeur : ElementRef : l'instance logique (ou presque)
   */
  private _ownedElements: Map<any,any> = null;



  /**
   * store the given component as last currentFocused
   * @param focusedCompo, the component to store
   */
  public wizRegisterFocus(focusedCompo: IWIZFocusable) {
    // console.log('wizRegisterFocus',focusedCompo);
    // pour empécher un élement déjà contenu dans les ownedElements de s'enregistrer comme currentFocused
    if (!this.focusedAlreadyAnOwnedElement(focusedCompo)) {
      this._currentFocused = focusedCompo;
      this._ownedElements = null;
    }

  }

  /**
   * verifie si l'élement en cours d'enregistrement est contenue dans un des ownedElements
   * @param focusedCompo, l'élement souhaitant s'enregistrer dans le focus manager
   * @return boolean, true si focusedCompo est contenu dans un des ownedElements, false sinon
   */
  private focusedAlreadyAnOwnedElement(focusedCompo: IWIZFocusable): boolean {
    // on renvoi false si pas de owned element
    if (!this._ownedElements || this._ownedElements.size === 0) {
      return false;
    }
    // boucle sur l'ensemble des ownedElements
    const owned = Array.from(this._ownedElements.keys()).find(element => this.isOwnedChild(element, focusedCompo));
    return !!owned;
  }

  /**
   * vérifie si focusedCompo est un childNode de ownedElement,
   * @param ownedElement, element asservis déjà répertorié dans ownedElements
   * @param focusedCompo, element à comparer au childNode du ownedElement
   */
  private isOwnedChild(ownedElement: ElementRef, focusedCompo: IWIZFocusable): boolean {

      // prise de l'elementRef  du focusedCompo
      const focusedCompoElementRef: ElementRef | null = ownedElement ? (focusedCompo as any).elementRef : null;
      if (!focusedCompoElementRef) {
        return false; // pas de compo actif
      }
      // si focusedCompoElementRef est l'un des ownedElements
      if (this._ownedElements.has(focusedCompoElementRef)) {
        return true;
      }
      // sinon, on remonte sur la pile de parentNode, à comparer avec le focusedCompoElementRef
      let node = focusedCompoElementRef?.nativeElement;
      while (node !== null) {
          if (node === ownedElement) {
            return true;
          }
          node = node.parentNode;
      }
      return false;
  }


  /**
   * enregistrement du composant enfant comme "asservi" au currentFocused (éviter la perte de focus)
   * This is really useful in case of overlay component that lives outside of the parent container
   */
  public wizRegisterOwner(pOwner: IWIZFocusable, pChild: ElementRef) {
    if (pOwner === this._currentFocused) {

      if(!this._ownedElements) {
        this._ownedElements = new Map<any,any>();
      }
      this._ownedElements.set(pChild.nativeElement, pChild);
      // console.log('wizRegisterOwner', pOwner, pChild, this._ownedElements);
    }
  }

  /**
   * release the given component as last currentFocused
   * @param focusedCompo, the component to store
   */
  public wizUnregisterFocus(focusedCompo: IWIZFocusable) {
    // console.log('wizUnregisterFocus',focusedCompo);
    if (this._currentFocused === focusedCompo) {
      this._currentFocused = null;
    }
  }

  ///////// global listening /////////////////

  /**
   * alimentation du "root"
   * @param root, la racine applicative
   */
  public setRoot(root: ElementRef) {
    this.root = root;
    // TODO all the global listening of the dom that will be usefull
    root.nativeElement.addEventListener('mousedown', e => this.mousedownHandler(e), true);
    root.nativeElement.addEventListener('keydown', e => this.keydownHandler(e));
    root.nativeElement.addEventListener('click', e => this.mouseClickHandler(e), true);
  }

  /**
   * Global mouse down handler.
   * If current component is in error and does not accept focus out on error,
   * this event and the sub sequent click event are prevented
   * @param e Mouse down event
   */
  private mousedownHandler(e:MouseEvent) {
    // Première étape, on détecte que le focus est destiné à sortir...
    if(this._currentFocused && !this.isCurrentFocused(e.target)) {
      // console.log('mousedownHandler', 'loosing focus',this._currentFocused, e.target);

      // suis-je en erreur ?
      if ( this._currentFocused.wizFocusMode &&
          !WizErrorManager.instance.isElementValid(this.currentFocused, undefined)) {
          this.finalizeFocusOut(e);
      } else { // non, pas d'erreur, libération
        this._currentFocused.wizLoseFocus();
        // Libération du clic outside
        this.clickOutsidePrevented = false;
      }

    }
    else {
      return; // tout est OK...
    }
  }

  /**
   * gestion de la navigation clavier
   * tab event is prevented
   * @param e Key down event
   */
  private keydownHandler(e:KeyboardEvent) {
    if(e.key === VALUE_TAB) { // seulement tab
      if (this._currentFocused) {
        // suis-je en erreur ?
        if ( this._currentFocused.wizFocusMode &&
          !WizErrorManager.instance.isElementValid(this.currentFocused, undefined)) {
        this.finalizeFocusOut(e);
        } else { // non, pas d'erreur, libération
          this._currentFocused.wizLoseFocus();
          // Libération du clic outside
          this.clickOutsidePrevented = false;
        }
      } // fin test nature du compo
    } // fin test Tab
  }

  /** fin de traitement sur sortie, application du mode de rollback */
  private finalizeFocusOut(e):void {
        // Focus out prevent behavior
        if (this._currentFocused.wizFocusMode === FOCUS_MODE.STOP) {
          // console.log('mousedown stopped', e);
          e.preventDefault();
          e.stopPropagation();
          this.clickOutsidePrevented = true;
          this._currentFocused.wizFocus();
        } else if (this._currentFocused.wizFocusMode === FOCUS_MODE.ROLLBACK) {
          WizErrorManager.instance.rollback(this.currentFocused);// rollback véritable sur le compo
          this._currentFocused.wizLoseFocus();
        } else { // mode PASS
          this._currentFocused.wizLoseFocus();
        }
  }

  /**
   * Test if a component is the current focused component or one of its children
   * @param pComp Tested component
   * @return boolean true if the current focused component is or contains the tested component
   */
  public isCurrentFocused(pComp: any): boolean {

    // prise de la part "physique"
    const nativeCurrentFocused: any = this._currentFocused ? (this._currentFocused as any).elementRef.nativeElement : null;
    if (!nativeCurrentFocused) {
      return false;// pas de compo actif
    }

    // sinon, on remonte sur la pile de parent, à comparer avec le compo ou ses "asservis"
    while (pComp) {

      // Cas ou le pComp est un elementRef car le isEqualNode à besoin d'un htmlElement
      if(pComp instanceof ElementRef) {
        pComp = pComp.nativeElement;
      }

      if (pComp.isEqualNode(nativeCurrentFocused)) { // c'est moi
        return true;
      }
      if (this._ownedElements && this._ownedElements.size > 0) {// j'ai des "enfants de focus"
        if (this._ownedElements.has(pComp)) { // c'est l'un des asservis
        // if (this.ownedsHasFocused(pComp)) {
          return true;
        }
      }
      // console.log('isCurrentFocused 4', pComp);
      if(pComp.parentElement.isEqualNode(this.root.nativeElement)){// jusqu'a la racine, pas plus
        pComp = null;
      } else {
        pComp = pComp.parentElement; // prise du parent
      }
    }
    // if (!result) {
    //   console.log('outside current focused elt ' + nativeCurrentFocused);
    // } else {
    //   console.log('inside current focused elt ' + nativeCurrentFocused);
    // }
    return false;
  }

  /**
   * Global click handler.
   * If mouse down has been prevented, this event is prevented
   * @param e Click event
   */
  mouseClickHandler(e:MouseEvent) {
    // console.log(this.clickOutsidePrevented);
    /* Ajout du test sur le current focus : globalement sans ce test,
      les compos non valides en mode STOP deviennent inertes vis à vis de la souris
      (exemples: boutons dans inputs, ouverture du calendar)
    */
    if (this.clickOutsidePrevented && !this.isCurrentFocused(e.target)) {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  /**
   * set focus to IWIZFocusable component
   * @param compo : component that takes focus
   * TODO: le timeout est un patch qui dois etre remplacé
   */
  public wizSetFocus(component: IWIZFocusable) {
    setTimeout(() => {
      // console.log('wizSetFocus', component);
      component.wizFocus();
    });
  }
}
